// src/components/ImageViewerModal.tsx
import React, { useCallback } from "react";
import styled from "styled-components";
import { FiDownload, FiX } from "react-icons/fi";

interface ImageViewerModalProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  open,
  imageUrl,
  onClose,
}) => {
  // always declare hooks first
  const handleDownload = useCallback(async () => {
    const filename = imageUrl.split("/").pop() || "download";
    console.log("↓ Download clicked:", imageUrl);

    // helper for fallback
    const fallbackLink = () => {
      console.log("↪️ falling back to native download");
      const a = document.createElement("a");
      a.href = imageUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    try {
      // try fetch→blob approach first
      const res = await fetch(imageUrl, { mode: "cors" });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      console.log("✅ download initiated via blob");
    } catch (err) {
      console.warn("⚠️ blob download failed:", err);
      // fallback to basic download link
      fallbackLink();
    }
  }, [imageUrl]);

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FiX size={20} />
        </CloseButton>
        <Image src={imageUrl} alt="Preview" />
        <DownloadButton onClick={handleDownload}>
          <FiDownload size={16} style={{ marginRight: 6 }} />
          Download
        </DownloadButton>
      </ModalContent>
    </Overlay>
  );
};

export default ImageViewerModal;

// Styled-components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalContent = styled.div`
  position: relative;
  background: #fff;
  padding: 1rem;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const CloseButton = styled.button`
  all: unset;
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
`;
const Image = styled.img`
  max-width: 100%;
  max-height: 80vh;
  margin-bottom: 1rem;
`;
const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: white;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover {
    background: #1e4bb8;
  }
`;
